import { format } from 'date-fns'

import { EMOJI_SEGMENT, SEGMENT } from '../../constants/communication'
import { composeSearchFilters } from '../../utils/compose-search-filters'
import { getFullName } from '../../utils/format-helpers'
import { useShopWithLinks } from '../../hooks/shopwith/use-shopwith-links'
import { useInfiniteData } from '../../hooks/use-infinite-data'
import { useBroadcasts } from './queries'

import type { ShopWithIds } from '../../hooks/templates/mutations'
import type { BroadcastValues } from './queries'
import type { BroadcastBase } from './broadcast.schema'

function checkForBlockedWords({
  strToCheck,
  blockedList,
}: {
  strToCheck?: string | null
  blockedList: Array<string>
}) {
  if (!strToCheck) return []
  const words = strToCheck.toLowerCase().split(/\W+/)
  const lcBlockedList = blockedList.map((word) => word.toLowerCase())
  const matchedWordsSet = new Set(
    words.filter((word) => lcBlockedList.includes(word))
  )

  return Array.from(matchedWordsSet)
}

function composeBroadcastFilters({
  admin,
  filters,
}: {
  admin?: boolean
  filters: Parameters<typeof composeSearchFilters>[0]
}) {
  return composeSearchFilters({
    admin,
    subscribersOnly: true,
    ...filters,
  })
}

function composeEmojiSummary(input: string, emojis: Array<string>) {
  const emojiIndices: Record<string, Array<number>> = {}

  emojis.forEach((emoji) => {
    const indices = []
    let currentIndex = input.indexOf(emoji)
    while (currentIndex !== -1) {
      indices.push(currentIndex)
      currentIndex = input.indexOf(emoji, currentIndex + 1)
    }
    emojiIndices[emoji] = indices
  })

  const summary = Object.entries(emojiIndices)
    .map(
      ([emoji, indices]) =>
        `${emoji} at position${indices.length > 1 ? 's' : ''} ${indices.join(
          ', '
        )}`
    )
    .join('; ')

  return summary
}

// TODO: TYPES ETC FROM AUTH CONTEXT
function composeFromFields({ user, account }: { user: any; account: any }) {
  let fromName: string | null = null
  let fromEmailName: string | null = null
  let fromEmailDomain: string | null = null

  if (user.hasSendGrid && account.sendgridDomains.length > 0) {
    fromName = user.displayName ?? getFullName(user)
    fromEmailName = user.twilioProxyEmail.split('@')[0]
    fromEmailDomain = user.twilioProxyEmail.split('@')[1]
  }

  if (user.hasSendGrid) {
    fromName = user.displayName ?? getFullName(user)
  }

  return { fromName, fromEmailName, fromEmailDomain }
}

function composeTitle(title?: string) {
  return title || `Broadcast ${format(new Date(), 'MM/dd/yy')}`
}

function getBroadcastFormValues(broadcast: BroadcastValues) {
  const {
    title,
    smsTemplateId,
    emailTemplateId,
    fromEmail,
    fromName,
    fromUserRule,
    imglyString,
    unlayerDesign,
  } = broadcast
  return {
    date: broadcast.date?.iso ?? null,
    title,

    smsTemplateId,
    emailTemplateId,

    fromEmailName: broadcast.fromEmail?.split('@')[0],
    fromEmailDomain: broadcast.fromEmail?.split('@')[1],
    fromEmail,
    fromName,
    fromUserRule,
    fromUserId: broadcast.fromUser?.objectId,

    messageType: broadcast.messageType || 'prioSmsEmail',

    smsMessage: broadcast.smsMessage ?? '',
    setAttachmentsSms: broadcast.attachmentsSms,
    collectionIdsSms: broadcast.collectionsSms?.map((c) => c.objectId),
    lookIdsSms: broadcast.looksSms?.map((l) => l.objectId),
    productIdsSms: broadcast.productsSms?.map((p) => p.objectId),
    imglyString,

    emailSubject: broadcast.emailSubject ?? '',
    emailMessage: broadcast.emailMessage ?? '',
    setAttachmentsEmail: broadcast.attachmentsEmail,
    collectionIdsEmail: broadcast.collectionsEmail?.map((c) => c.objectId),
    lookIdsEmail: broadcast.looksEmail?.map((l) => l.objectId),
    productIdsEmail: broadcast.productsEmail?.map((p) => p.objectId),
    unlayerDesign,

    /**
     * unlayerTemplateId - Possible Upcoming Feature
     */
  }
}

function getBroadcastPreviewImage(broadcast: BroadcastBase) {
  if (broadcast.status === 'draft') {
    return broadcast.attachmentsSms?.[0] || broadcast.unlayerImage || null
  } else if (broadcast.status === 'success') {
    if (broadcast.messageType === 'email') {
      return broadcast.unlayerImage || broadcast.attachmentsEmail?.[0] || null
    } else if (broadcast.messageType === 'sms') {
      return broadcast.attachmentsSms?.[0] || null
    } else if (broadcast.messageType === 'prioSmsEmail') {
      return broadcast.attachmentsSms?.[0] || broadcast.unlayerImage || null
    } else return null
  } else {
    // For other statuses, keep original behavior
    return broadcast.attachmentsSms?.[0] || broadcast.unlayerImage || null
  }
}

function getBroadcastPreviewText(broadcast: BroadcastBase) {
  if (broadcast.status === 'draft') {
    return broadcast.smsMessage || broadcast.emailMessage || null
  } else if (broadcast.status === 'success') {
    if (broadcast.messageType === 'email') {
      return broadcast.emailSubject || broadcast.title || null
    } else if (broadcast.messageType === 'sms') {
      return broadcast.smsMessage || broadcast.title || null
    } else if (broadcast.messageType === 'prioSmsEmail') {
      return broadcast.smsMessage || broadcast.emailMessage || null
    } else return null
  } else {
    // For other statuses, keep original behavior
    return broadcast.smsMessage || broadcast.emailMessage || null
  }
}

function getMaxCharacterCount(
  segmentLimit: number,
  { isNonGsm }: { isNonGsm: boolean }
) {
  return isNonGsm ? EMOJI_SEGMENT * segmentLimit : SEGMENT * segmentLimit
}

function useComposeShopWithLink(ids?: Partial<ShopWithIds>) {
  const { generateShopWithLinkPreview } = useShopWithLinks()

  return ids?.productIds?.length
    ? generateShopWithLinkPreview('productIds', ids?.productIds)
    : ids?.lookIds?.length
    ? generateShopWithLinkPreview('lookIds', ids?.lookIds)
    : ids?.collectionIds?.length
    ? generateShopWithLinkPreview('collectionIds', ids?.collectionIds)
    : ''
}

function useMyBroadcasts() {
  const { data, ...broadcastsQuery } = useBroadcasts()
  const flatBroadcasts = data?.pages.flatMap((x) => x.data) ?? []

  const [ref, loadMoreBroadcasts] = useInfiniteData(
    broadcastsQuery.fetchNextPage
  )

  return {
    broadcasts: flatBroadcasts,
    ref,
    loadMoreBroadcasts,
    ...broadcastsQuery,
  }
}

export {
  checkForBlockedWords,
  composeBroadcastFilters,
  composeEmojiSummary,
  composeFromFields,
  composeTitle,
  getBroadcastFormValues,
  getBroadcastPreviewImage,
  getBroadcastPreviewText,
  getMaxCharacterCount,
  useMyBroadcasts,
  useComposeShopWithLink,
}
